import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Radio, Globe, MessageCircle, Rocket, BadgeCheck } from "lucide-react";
import JoinNotification from "@/components/home/JoinNotification";
import { PHYNTEX_REGISTER, TELEGRAM_PM, CHANNEL_ASYROL } from "@/constants/links";

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.15 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } },
};

const glassButtonClass =
  "flex items-center gap-[clamp(10px,2vw,14px)] w-full px-[clamp(16px,3.2vw,22px)] py-[clamp(13px,2.2vh,18px)] rounded-xl bg-white/[0.03] border border-white/10 backdrop-blur-md text-white font-inter text-[clamp(13px,2vh,16px)] font-medium hover:bg-white/[0.08] transition-all duration-300 neon-dot-frame";

const HomePage = () => {
  return (
    <div className="relative h-screen w-full overflow-hidden bg-[#050505]" data-testid="home-page">
      {/* Fixed video background */}
      <div className="fixed inset-0 z-0 overflow-hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover"
          poster="/assets/dp1.jpg"
          data-testid="home-bg-video"
        >
          <source src="/assets/intro.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-[#050505]/85" />
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className="relative z-10 h-full w-full flex flex-col items-center justify-center gap-[clamp(16px,2.8vh,32px)] px-5 py-4"
      >
        {/* Profile Photo */}
        <motion.div variants={itemVariants} className="relative" data-testid="home-profile">
          <div
            className="relative w-[clamp(100px,18vh,164px)] h-[clamp(100px,18vh,164px)] rounded-[24px] overflow-hidden"
            style={{
              boxShadow: "0 0 24px rgba(57,255,20,0.25), 0 0 48px rgba(57,255,20,0.1), 0 8px 32px rgba(0,0,0,0.5)",
            }}
          >
            <img
              src="/assets/dp2.jpg"
              alt="Helloasyrol"
              className="w-full h-full object-cover"
              data-testid="home-profile-photo"
            />
            <div
              className="absolute inset-0 rounded-[24px]"
              style={{
                border: "1.5px solid rgba(57,255,20,0.2)",
              }}
            />
          </div>
        </motion.div>

        {/* Name + Bio Section */}
        <motion.div variants={itemVariants} className="flex flex-col items-center gap-[clamp(10px,1.6vh,16px)]">
          {/* Name with verified badge */}
          <div className="flex items-center gap-2" data-testid="home-name">
            <h1
              className="font-teko text-[clamp(28px,5.2vh,44px)] font-bold text-white uppercase tracking-wider leading-none"
              style={{
                textShadow: "0 0 20px rgba(255,255,255,0.15)",
              }}
            >
              HELLOASYROL
            </h1>
            <BadgeCheck
              className="w-[clamp(18px,3vh,26px)] h-[clamp(18px,3vh,26px)] mt-[2px]"
              style={{ color: "#39FF14", filter: "drop-shadow(0 0 4px rgba(57,255,20,0.5))" }}
              data-testid="home-verified-badge"
            />
          </div>

          {/* Bio tags */}
          <div className="flex flex-wrap items-center justify-center gap-2" data-testid="home-tagline">
            <span className="bio-tag">
              <span style={{ color: "#39FF14", fontSize: "8px" }}>&#9679;</span>
              Funded Trader
            </span>
            <span className="bio-tag">
              <span style={{ color: "#4FC3F7", fontSize: "8px" }}>&#9679;</span>
              Entrepreneur
            </span>
            <span className="bio-tag">
              <span style={{ color: "#FFD93D", fontSize: "8px" }}>&#9679;</span>
              Market Analyst
            </span>
          </div>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div variants={itemVariants} className="w-full max-w-[420px] flex flex-col gap-[clamp(10px,1.8vh,16px)]">
          <a
            href={CHANNEL_ASYROL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="btn-channel-asyrol"
            className={glassButtonClass}
            style={{ "--dot-color": "#FFFFFF", "--dot-delay": "-0.6s" }}
          >
            <Radio className="w-[clamp(17px,2.6vh,22px)] h-[clamp(17px,2.6vh,22px)] shrink-0" />
            CHANNEL ASYROL
          </a>

          <Link
            to="/landing"
            data-testid="btn-landing-page"
            className={glassButtonClass}
            style={{ "--dot-color": "#FFFFFF", "--dot-delay": "-1.2s" }}
          >
            <Globe className="w-[clamp(17px,2.6vh,22px)] h-[clamp(17px,2.6vh,22px)] shrink-0" />
            WHO IS HELLOASYROL ?
          </Link>

          <a
            href={TELEGRAM_PM}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="btn-free-community"
            className="flex items-center gap-[clamp(10px,2vw,14px)] w-full px-[clamp(16px,3.2vw,22px)] py-[clamp(13px,2.2vh,18px)] rounded-xl border border-[#39FF14] text-[#39FF14] font-inter text-[clamp(13px,2vh,16px)] font-semibold bg-[#050505] hover:bg-[#39FF14]/10 transition-all duration-300 neon-dot-frame"
            style={{ "--dot-color": "#39FF14", "--dot-delay": "-1.8s" }}
          >
            <MessageCircle className="w-[clamp(17px,2.6vh,22px)] h-[clamp(17px,2.6vh,22px)] shrink-0" />
            FREE GROUP COMMUNITY
          </a>

          <a
            href={PHYNTEX_REGISTER}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="btn-register-now"
            className="flex items-center gap-[clamp(10px,2vw,14px)] w-full px-[clamp(16px,3.2vw,22px)] py-[clamp(13px,2.2vh,18px)] rounded-xl text-white font-inter text-[clamp(13px,2vh,16px)] font-bold transition-all duration-300 neon-dot-frame"
            style={{
              backgroundImage:
                "linear-gradient(135deg, rgba(0,0,0,0.75), rgba(0,0,0,0.75)), url('https://helloasyrol.b-cdn.net/IMG_4939.JPG')",
              backgroundSize: "cover",
              backgroundPosition: "center",
              boxShadow: "0 0 20px rgba(79,195,247,0.35)",
              "--dot-color": "#4FC3F7",
              "--dot-delay": "-2.4s",
            }}
          >
            <Rocket className="w-[clamp(17px,2.6vh,22px)] h-[clamp(17px,2.6vh,22px)] shrink-0" />
            NO DEMO NO DUMMY — REGISTER NOW
          </a>
        </motion.div>
      </motion.div>

      {/* Social proof notification */}
      <JoinNotification />
    </div>
  );
};

export default HomePage;
